import invariant from 'invariant';
import defined from '~app/core/defined';
import { setPosition } from '~app/core/positionUtils';
import Position from '~app/models/Position';
import { signalConnectionsValid } from '~app/models/DiagramState';
import { CANVAS_SIZE } from '~app/features/canvas/consts';
import lodash from 'lodash';
import * as Arrows from './arrowCalc';
import { DEFAULT_ARROW_HEAD_OFFSET, DEFAULT_ARROW_TAIL_OFFSET } from './consts';
import { v4 } from 'uuid';
// import { current } from '@reduxjs/toolkit';

const graphModelMap = {
  graph: 'graph',
  conceptual: 'conceptual',
  causal: 'causal',
};

function checkBounds(position) {
  if (position.x < 0) position.x = 0;
  else if (position.x >= CANVAS_SIZE) position.x = CANVAS_SIZE - 1;
  if (position.y < 0) position.y = 0;
  else if (position.y >= CANVAS_SIZE) position.y = CANVAS_SIZE - 1;
}

function createNode(type, x, y) {
  const node = { type, position: { x, y } };
  checkBounds(node.position);
  if (type === 'flow') {
    const position = new Position({ x, y });
    node.arrowHeadPosition = position
      .add(DEFAULT_ARROW_HEAD_OFFSET)
      .serialize();
    checkBounds(node.arrowHeadPosition);
    node.arrowTailPosition = position
      .add(DEFAULT_ARROW_TAIL_OFFSET)
      .serialize();
    checkBounds(node.arrowTailPosition);
  }
  return node;
}

// Drop a label from the label bank onto the canvas.
export const placeLabel = (inst, { label, x, y }) => {
  invariant(inst.labels[label], `no such label ${label}`);
  invariant(!inst.workingModel.nodes[label], `label ${label} already placed`);
  inst.workingModel.nodes[label] = createNode(
    graphModelMap[inst.type] ? 'generic' : 'unassigned',
    x,
    y,
  );
};

// Move a node.
export const moveNode = (inst, { label, x, y, direction, value }) => {
  const { nodes } = inst.workingModel;
  const node = nodes[label];
  invariant(node, `label ${label} not placed`);

  // Keyboard controlled movement
  if (direction && value) {
    setPosition(node.position, direction, value);
  }
  // Click and drag
  else {
    node.position.x = x;
    node.position.y = y;
  }
  checkBounds(node.position);
  updateArrowsForLabel(inst, label);
};

function updateArrowsForLabel(inst, label) {
  const { nodes, signals } = inst.workingModel;
  Object.keys(nodes)
    .filter(
      n =>
        nodes[n].type === 'flow' &&
        (n === label ||
          nodes[n].connectTo === label ||
          nodes[n].connectFrom === label),
    )
    .forEach(n => Arrows.calcFlowArrow(inst, n));
  signals
    .filter(s => s.connectTo === label || s.connectFrom === label)
    .forEach(s => Arrows.calcSignalArrow(inst, s));
}

function disconnectFlowFrom(node, label) {
  let changed = false;
  if (node.connectTo === label) {
    node.connectTo = null;
    node.arrowHeadPosition = new Position(node.position)
      .add(DEFAULT_ARROW_HEAD_OFFSET)
      .serialize();
    changed = true;
  }
  if (node.connectFrom === label) {
    node.connectFrom = null;
    node.arrowTailPosition = new Position(node.position)
      .add(DEFAULT_ARROW_TAIL_OFFSET)
      .serialize();
    changed = true;
  }
  return changed;
}

export const moveFlowArrow = (
  inst,
  { label, arrow, x, y, direction, value },
) => {
  const node = inst.workingModel.nodes[label];
  invariant(node, `label ${label} not placed`);

  // Keyboard controlled movement
  if (direction && value) {
    if (arrow === 'head' && node.arrowHeadPosition) {
      setPosition(node.arrowHeadPosition, direction, value);
      checkBounds(node.arrowHeadPosition);
    }
    if (arrow === 'tail' && node.arrowTailPosition) {
      setPosition(node.arrowTailPosition, direction, value);
      checkBounds(node.arrowTailPosition);
    }
  }
  // Click and drag
  else {
    if (arrow === 'head' && node.arrowHeadPosition) {
      node.arrowHeadPosition.x = x;
      node.arrowHeadPosition.y = y;
      checkBounds(node.arrowHeadPosition);
    }
    if (arrow === 'tail' && node.arrowTailPosition) {
      node.arrowTailPosition.x = x;
      node.arrowTailPosition.y = y;
      checkBounds(node.arrowTailPosition);
    }
  }
  Arrows.calcFlowArrow(inst, label);
};

// Delete a node (returning the label to the label bank)
export const deleteNode = (inst, { label }) => {
  invariant(inst.workingModel.nodes[label], `label ${label} not placed`);

  const { nodes, signals } = inst.workingModel;
  for (const key of Object.keys(nodes)) {
    if (disconnectFlowFrom(nodes[key], label)) {
      Arrows.calcFlowArrow(inst, key);
    }
  }
  delete nodes[label];
  delete inst.arrowPositions.paths[label];
  const remove = signals.filter(
    s => s.connectFrom === label || s.connectTo === label,
  );
  if (remove.length > 0) {
    remove.forEach(
      s => delete inst.arrowPositions.paths[`${s.connectFrom}>${s.connectTo}`],
    );
    inst.workingModel.signals = signals.filter(
      s => s.connectFrom !== label && s.connectTo !== label,
    );
  }
  if (inst.editMode === 'solution') {
    delete inst.labels[label];
  }
};

// Reset the model (return to inital model)
export const resetModel = inst => {
  if (inst.editMode === 'solution') {
    inst.workingModel = inst.solutionModel;
  } else {
    inst.workingModel = inst.initialModel;
  }
};

// Load an external working model
export const loadWorkingModel = (inst, newWorkingModel) => {
  inst.workingModel = newWorkingModel;
  Arrows.init(inst);
};

// Assign or reassign a node type.
export const assignNodeType = (inst, { label, type }) => {
  invariant(inst.workingModel.nodes[label], `label ${label} not placed`);

  if (type === inst.workingModel.nodes[label].type) return inst; // noop

  const { nodes, signals } = inst.workingModel;
  const isInErrorState = inst.workingModel.nodes[label]?.hasError;

  if (nodes[label].type === 'stock') {
    // Current type is stock (so new type is something else) so need
    // to disconnect any flows connected to me
    for (const key of Object.keys(nodes).filter(k => k !== label)) {
      if (disconnectFlowFrom(nodes[key], label)) {
        Arrows.calcFlowArrow(inst, key);
      }
    }
  }
  nodes[label] = createNode(
    type,
    nodes[label].position.x,
    nodes[label].position.y,
  );
  if (type === 'flow') {
    Arrows.calcFlowArrow(inst, label);
  }
  const remove = signals.filter(
    s => !signalConnectionsValid(s, inst.workingModel),
  );
  if (remove.length > 0) {
    remove.forEach(
      s => delete inst.arrowPositions.paths[`${s.connectFrom}>${s.connectTo}`],
    );
    inst.workingModel.signals = signals.filter(s => remove.indexOf(s) < 0);
  }

  if (isInErrorState) {
    inst.workingModel.nodes[label].hasError = true;
    inst.workingModel.nodes[label].pendingRecheck = { node: true };
  }
};

// Connect or disconnect flow specified by flowLabel.
// If fromLabel or toLabel are absent (undefined) the corresponding connection will be unchanged.
// Otherwise, null disconnects the corresponding connection and a string value connects to the
// node named by the string value.
export const connectFlow = (inst, { flowLabel, fromLabel, toLabel }) => {
  const node = inst.workingModel.nodes[flowLabel];
  const hasPendingRecheck = node?.pendingRecheck;
  invariant(node?.type === 'flow', `flowLabel ${flowLabel} is not a flow`);
  invariant(
    !fromLabel || inst.workingModel.nodes[fromLabel]?.type === 'stock',
    `fromLabel ${fromLabel} is not a stock`,
  );
  invariant(
    !toLabel || inst.workingModel.nodes[toLabel]?.type === 'stock',
    `toLabel ${toLabel} is not a stock`,
  );

  if (fromLabel !== undefined) {
    node.connectFrom = fromLabel;
    if (fromLabel) {
      node.arrowTailPosition = null;
    } else if (!node.arrowTailPosition) {
      node.arrowTailPosition = new Position(node.position)
        .add(DEFAULT_ARROW_TAIL_OFFSET)
        .serialize();
      checkBounds(node.arrowTailPosition);
    }
    //since connectFrom was changed, mark any error markings as pending
    if (hasPendingRecheck) {
      node.pendingRecheck.flowSource = true;
    } else {
      node.pendingRecheck = { flowSource: true };
    }
  }
  if (toLabel !== undefined) {
    node.connectTo = toLabel;
    if (toLabel) {
      node.arrowHeadPosition = null;
    } else if (!node.arrowHeadPosition) {
      node.arrowHeadPosition = new Position(node.position)
        .add(DEFAULT_ARROW_HEAD_OFFSET)
        .serialize();
      checkBounds(node.arrowHeadPosition);
    }
    //since connectTo was changed, mark any error markings as pending
    if (hasPendingRecheck) {
      node.pendingRecheck.flowSink = true;
    } else {
      node.pendingRecheck = { flowSink: true };
    }
  }
  Arrows.calcFlowArrow(inst, flowLabel);
};

function createSignal(inst, fromLabel, toLabel, polarity) {
  const fromNode = inst.workingModel.nodes[fromLabel];
  const toNode = inst.workingModel.nodes[toLabel];
  // TODO: figure out a better way to position the new signal instead of just midway between
  const position = new Position(fromNode.position)
    .lerp(toNode.position, 0.5)
    .serialize();
  return {
    polarity,
    connectFrom: fromLabel,
    connectTo: toLabel,
    position,
  };
}

function findSignal(inst, fromLabel, toLabel, polarity = null) {
  invariant(
    inst.workingModel.nodes[fromLabel],
    `fromLabel ${fromLabel} does not exist`,
  );
  invariant(
    inst.workingModel.nodes[toLabel],
    `toLabel ${toLabel} does not exist`,
  );
  return inst.workingModel.signals.findIndex(s => {
    if (polarity === '0') {
      if (
        (s.connectFrom === fromLabel && s.connectTo === toLabel) ||
        (s.connectFrom === toLabel && s.connectTo === fromLabel)
      ) {
        return true;
      }
    } else {
      if (s.connectFrom === fromLabel && s.connectTo === toLabel) {
        return true;
      }
    }
    return false;
  });
}

// Add a signal, or modify the signal's polarity if it exists
export const addSignal = (inst, { fromLabel, toLabel, polarity }) => {
  const signals = inst.workingModel.signals;
  let index = findSignal(inst, fromLabel, toLabel, polarity);
  if (index >= 0) {
    // signal exists, just update its polarity
    signals[index].polarity = polarity;
    //if signal is marked with an error and we change the sign,
    //mark it for pending recheck
    if (signals[index].hasError) {
      signals[index].pendingRecheck = true;
    }
  } else {
    const sig = createSignal(inst, fromLabel, toLabel, polarity);
    invariant(
      signalConnectionsValid(sig, inst.workingModel),
      'incorrect signal connection',
    );
    signals.push(sig);
    Arrows.calcSignalArrow(inst, sig);
  }
};

// Move signal
export const moveSignal = (
  inst,
  { fromLabel, toLabel, x, y, direction, value },
) => {
  const index = findSignal(inst, fromLabel, toLabel, null);
  invariant(index >= 0, `signal ${fromLabel} -> ${toLabel} not found`);

  // Keyboard controlled movement
  if (direction && value) {
    setPosition(inst.workingModel.signals[index].position, direction, value);
  }
  // Click and drag
  else {
    inst.workingModel.signals[index].position.x = x;
    inst.workingModel.signals[index].position.y = y;
  }
  checkBounds(inst.workingModel.signals[index].position);
  Arrows.calcSignalArrow(inst, inst.workingModel.signals[index]);
};

// Delete a signal
export const deleteSignal = (inst, { fromLabel, toLabel }) => {
  const index = findSignal(inst, fromLabel, toLabel, null);
  invariant(index >= 0, `signal ${fromLabel} -> ${toLabel} not found`);
  inst.workingModel.signals.splice(index, 1);
  delete inst.arrowPositions.paths[`${fromLabel}>${toLabel}`];
};

/*** Edit mode stuff ***/

export const setName = (inst, payload) => {
  inst.name = payload;
};

export const updateInstructions = (inst, payload) => {
  inst.instructions = payload;
};

export const updateHeader = (inst, payload) => {
  inst.instructionHeader = payload;
};

export const createAndPlaceLabel = (inst, { x, y }) => {
  invariant(
    inst.editMode === 'solution',
    'createLabel only valid for solution edit mode',
  );
  const allTexts = new Set(Object.values(inst.labels));
  let newLabelText = 'Object 1';
  let counter = 1;
  while (allTexts.has(newLabelText)) {
    newLabelText = `Object ${counter}`;
    counter++;
  }
  let label = 'object1';
  counter = 1;
  while (label in inst.labels) {
    counter++;
    label = `object${counter}`;
  }
  inst.labels[label] = newLabelText;
  inst.allLabelBankItems.push(label);
  if (inst.labelOrder === 'alpha') {
    inst.allLabelBankItems.sort((a, b) =>
      inst?.labels[a] ? inst.labels[a].localeCompare(inst.labels[b]) : 0,
    );
  } else if (inst.labelOrder.startsWith('fixed:')) {
    inst.labelOrder = 'fixed:' + inst.allLabelBankItems.join(',');
  }

  placeLabel(inst, { label, x, y });
};

export const updateLabelText = (inst, { label, text }) => {
  invariant(
    inst.editMode === 'solution',
    'updateLabelText only valid for solution edit mode',
  );
  invariant(label in inst.labels, `no such label ${label}`);
  if (
    Object.keys(inst.labels)
      .filter(l => l !== label)
      .some(l => inst.labels[l].localeCompare(text) === 0)
  ) {
    // duplicates not allowed
    // TODO: some kind of feedback is needed
    inst.duplicateName = true;
    return;
  }
  inst.labels[label] = text;
};

export const resetDuplicateName = inst => {
  inst.duplicateName = false;
};

export const updateAnchorDataImpl = (inst, { label, data }) => {
  invariant(label in inst.labels, `no such label ${label}`);
  if (!defined(inst.arrowAnchors)) inst.arrowAnchors = {};
  inst.arrowAnchors[label] = data;
  updateArrowsForLabel(inst, label);
};

//Add error status (either type error or flow input or output error) to object/node
const addErrorToObject = (inst, label, errorType) => {
  if (errorType === 'connectFromError') {
    inst.workingModel.nodes[label].hasSourceError = true;
  } else if (errorType === 'connectToError') {
    inst.workingModel.nodes[label].hasSinkError = true;
  } else {
    inst.workingModel.nodes[label].hasError = true;
  }
};

//Remove error status (either type error or flow input or output error) to object/node
const removeErrorFromObject = (inst, label, errorType) => {
  if (errorType === 'connectFromError') {
    delete inst.workingModel.nodes[label].hasSourceError;
  } else if (errorType === 'connectToError') {
    delete inst.workingModel.nodes[label].hasSinkError;
  } else {
    delete inst.workingModel.nodes[label].hasError;
  }
};

//Set error counts
const setWorkingModelErrors = (inst, objWMErrors) => {
  inst.workingModelErrors = Object.assign({}, objWMErrors);
};

//Tabluate errors and mark erroneous elements in workspace/canvas
export const tabulateErrors = inst => {
  const workingNodes = inst.workingModel.nodes;
  const solutionNodes = inst.connectedSolutionObjects;
  const workingSignals = inst.workingModel.signals; // this stores the workspace connections
  const labels = inst.labels;

  // connectorProps are properties of objects that have pos(+), neg(-), neutral(n) connections between them.
  // This is the actual connection solution
  const connectorProps = {
    sorted: new Set(Object.values(inst.solutionConnectorProps.sorted)), // probably to verify neutral connectors? direction is not preserved
    unsorted: new Set(Object.values(inst.solutionConnectorProps.unsorted)), // probably to verify +/- connectors? direction is preserved
    justTerminals: new Set(
      Object.values(inst.solutionConnectorProps.sortedTerminals),
    ),
    unsortedTerminals: new Set(
      Object.values(inst.solutionConnectorProps.unsortedTerminals),
    ),
    shortCycle: new Set(),
    unsortedShortCycle: new Set(),
  };

  // a copy of connector props terminals to keep track of missing connections
  const connectorPropsTerminals = {
    sorted: new Set(Object.values(inst.solutionConnectorProps.sorted)), // probably to verify neutral connectors? direction is not preserved
    unsorted: new Set(Object.values(inst.solutionConnectorProps.unsorted)), // probably to verify +/- connectors? direction is preserved
    justTerminals: new Set(
      Object.values(inst.solutionConnectorProps.sortedTerminals),
    ),
    unsortedTerminals: new Set(
      Object.values(inst.solutionConnectorProps.unsortedTerminals),
    ),
    shortCycle: new Set(),
    unsortedShortCycle: new Set(),
  };

  const solutionSignalCount = connectorProps.unsorted.size; // gives the number of +/-/n connections
  const arrSolutionNodeKeys = Object.keys(solutionNodes);
  const missingObjects = arrSolutionNodeKeys.filter(
    nodeName => !(nodeName in workingNodes),
  );

  const workingFlowNodes = lodash.flow([
    Object.entries,
    arr => arr.filter(([key, value]) => value.type === 'flow'),
    Object.fromEntries,
  ])(workingNodes);

  let objWMErrors = {};
  const objectMistakes = [];
  const flowFromMistakes = [];
  const flowToMistakes = [];
  let missingObjectsCount = missingObjects.length;
  let connectorMistakeCount = 0;
  let missingConnectorCount = 0;
  let correctWorkingSignalCount = 0;
  let correctTerminalsWrongConnectorTypeCount = 0;
  let connectorMistakes = [];
  const missingConnectors = [];

  //Tally flow link errors for any flow objects in bank
  for (const property in missingObjects) {
    const missingObject = solutionNodes[missingObjects[property]];
    //If missing object should be a flow node, also add to flow link mistakes
    if (missingObject.type === 'flow') {
      flowFromMistakes.push(labels[missingObjects[property]]);
      flowToMistakes.push(labels[missingObjects[property]]);
    }
  }

  //Verify objects/nodes
  for (const property in workingNodes) {
    workingNodes[property].pendingRecheck = null;
    if (Object.prototype.hasOwnProperty.call(solutionNodes, property)) {
      if (workingNodes[property].type !== solutionNodes[property].type) {
        addErrorToObject(inst, property);
        objectMistakes.push(labels[property]);
        //If object should be a flow node, also add to flow link mistakes
        if (solutionNodes[property].type === 'flow') {
          // Flow node assigned to wrong type gets marked
          // with both flow from and flow to mistakes
          // regardless of whether it should be connected to or from a stock,
          // since a correctly unconnected from or to terminal earns one point
          // and a mis-assigned flow does not deserve any connection points
          flowFromMistakes.push(labels[property]); // fixes unassigned objects and
          flowToMistakes.push(labels[property]); // all variables unconnected
        }
      } else {
        removeErrorFromObject(inst, property);
        if (workingNodes[property].type === 'flow') {
          const linkFrom = workingNodes[property]?.connectFrom || null;
          const linkTo = workingNodes[property]?.connectTo || null;
          if (linkFrom === solutionNodes[property].connectFrom) {
            removeErrorFromObject(inst, property, 'connectFromError');
          } else {
            addErrorToObject(inst, property, 'connectFromError');
            flowFromMistakes.push(labels[property]);
          }
          if (linkTo === solutionNodes[property].connectTo) {
            removeErrorFromObject(inst, property, 'connectToError');
          } else {
            addErrorToObject(inst, property, 'connectToError');
            flowToMistakes.push(labels[property]);
          }
        }
      }
    }
    //else object/node in workspace not part of solution (a decoy object)
    else {
      //decoys in workspace are only errors if they are connected to another object
      //and only objects assigned a type can be connected, so we first test for that
      if (workingNodes[property].type !== 'unassigned') {
        let decoyObjectConnected = false;
        //see if decoy is a flow and is connected to another object
        if (
          workingNodes[property].type === 'flow' &&
          (workingNodes[property]?.connectFrom ||
            workingNodes[property]?.connectTo)
        ) {
          decoyObjectConnected = true;
        }
        //if not a connected flow, see if it's connected to a signal
        if (!decoyObjectConnected) {
          for (const signal of Object.entries(workingSignals)) {
            if (
              signal[1].connectFrom === property ||
              signal[1].connectTo === property
            ) {
              decoyObjectConnected = true;
              break;
            }
          }
        }

        //if not already erroneous, check for connection to/from a flow object
        //only stocks can be source or sink for flows, so we can guard for that
        if (!decoyObjectConnected && workingNodes[property].type === 'stock') {
          for (const flowNode of Object.entries(workingFlowNodes)) {
            if (
              workingNodes[flowNode[0]]?.connectFrom === property ||
              workingNodes[flowNode[0]]?.connectTo === property
            ) {
              decoyObjectConnected = true;
              break;
            }
          }
        }
        //if decoy object is connected to another object, mark it as an error
        if (decoyObjectConnected) {
          addErrorToObject(inst, property);
          objectMistakes.push(labels[property]);
        } else {
          removeErrorFromObject(inst, property);
        }
      }
      //if decoy object has no type, it is not an error
      //(untyped objects can't connect to other objects)
      else {
        removeErrorFromObject(inst, property);
      }
    }
  }

  // *************** track missing connectors start ***************
  // identify the objects that have short loop cycle - to do this - for each justTerminals check if opposite exists in unsortedTerminals
  // this check ignores the conceptual model and short cycles will always be present in such case
  if (
    connectorProps.justTerminals.size < connectorProps.unsortedTerminals.size
  ) {
    connectorProps.justTerminals.forEach(entry => {
      const [solutionConnectFrom, solutionConnectTo] = JSON.parse(entry);
      if (
        connectorProps.unsortedTerminals.has(
          JSON.stringify([solutionConnectTo, solutionConnectFrom]),
        )
      ) {
        connectorProps.shortCycle.add(entry);
        connectorPropsTerminals.shortCycle.add(entry);
        // connectorPropsTerminals.justTerminals will not have shortcycle related objects
        connectorPropsTerminals.justTerminals.delete(entry);
      }
    });
  }

  // build unsortedShortCycle Set
  connectorProps.unsorted.forEach(entry => {
    const parsedConnector = JSON.parse(entry);
    if (
      connectorProps.shortCycle.has(
        JSON.stringify([...parsedConnector].slice(1).sort()),
      )
    ) {
      connectorProps.unsortedShortCycle.add(entry);
      connectorPropsTerminals.unsortedShortCycle.add(entry);
    }
  });
  // *************** track missing connectors end ***************

  //Verify connectors/signals
  for (const signal in workingSignals) {
    const signalProps = [
      workingSignals[signal].polarity,
      workingSignals[signal].connectFrom,
      workingSignals[signal].connectTo,
    ];
    const connectorPath = `${labels[workingSignals[signal].connectFrom]} to ${
      labels[workingSignals[signal].connectTo]
    }`;
    workingSignals[signal].pendingRecheck = null;

    // *************** track missing connectors start ***************
    // for shortcycle eliminate connections in unsorted short cycle set
    if (
      connectorProps.shortCycle.has(
        JSON.stringify([...signalProps].slice(1).sort()),
      )
    ) {
      connectorPropsTerminals.unsortedShortCycle.forEach(entry => {
        // delete entry in the unsorted short cycle set
        const parsedConnector = JSON.parse(entry);
        if (
          signalProps[1] === parsedConnector[1] &&
          signalProps[2] === parsedConnector[2]
        ) {
          connectorPropsTerminals.unsortedShortCycle.delete(entry);
        }
      });
    }
    // for rest objects (with no short loop) - delete from justTerminals
    // remaining in justTerminals will be missing connectors
    // for polarity and direction find in unsorted
    else {
      if (
        connectorProps.justTerminals.has(
          JSON.stringify([...signalProps].slice(1).sort()),
        )
      ) {
        connectorPropsTerminals.justTerminals.delete(
          JSON.stringify([...signalProps].slice(1).sort()),
        );
      }
    }
    // *************** track missing connectors end ***************

    //if connector is neutral, confirm solution has neutral connector with
    //same terminals, regardless of direction
    if (workingSignals[signal].polarity === '0') {
      if (connectorProps.sorted.has(JSON.stringify([...signalProps].sort()))) {
        correctWorkingSignalCount++;
        delete workingSignals[signal].hasError;
      } else {
        workingSignals[signal].hasError = true;
        connectorMistakeCount++;
        //test to see if terminals are correct but only polarity wrong
        if (
          connectorProps.justTerminals.has(
            JSON.stringify([...signalProps].slice(1).sort()),
          )
        ) {
          correctTerminalsWrongConnectorTypeCount++;
          connectorMistakes.push(`${connectorPath} (polarity)`);
        } else {
          connectorMistakes.push(`${connectorPath}`);
        }
      }
    }
    //if connector is a positive or negative signal, confirm solution has
    //connector with same signal, same sender, same receiver
    else {
      if (connectorProps.unsorted.has(JSON.stringify(signalProps))) {
        correctWorkingSignalCount++;
        delete workingSignals[signal].hasError;
      } else {
        workingSignals[signal].hasError = true;
        connectorMistakeCount++;
        //test to see if terminals are correct but only polarity or direction is wrong
        if (
          connectorProps.justTerminals.has(
            JSON.stringify([...signalProps].slice(1).sort()),
          )
        ) {
          correctTerminalsWrongConnectorTypeCount++;
          connectorMistakes.push(`${connectorPath} (polarity or direction)`);
        } else {
          connectorMistakes.push(`${connectorPath}`);
        }
      }
    }
  }

  // *************** track missing connectors start ***************

  connectorProps.unsorted.forEach(entry => {
    const [sign, obj1, obj2] = JSON.parse(entry);
    if (
      connectorPropsTerminals.justTerminals.has(
        JSON.stringify([sign, obj1, obj2].slice(1).sort()),
      )
    ) {
      if (sign === '0') {
        missingConnectors.push(`${labels[obj1]} with ${labels[obj2]}`);
      } else {
        missingConnectors.push(
          `(${sign}ve) ${labels[obj1]} to ${labels[obj2]}`,
        );
      }
    }
  });

  connectorPropsTerminals.unsortedShortCycle.forEach(entry => {
    const [sign, obj1, obj2] = JSON.parse(entry);
    if (sign !== '0') {
      missingConnectors.push(`(${sign}ve) ${labels[obj1]} to ${labels[obj2]}`);
    }
  });

  // *************** track missing connectors end ***************

  objWMErrors = {
    objectMistakes: objectMistakes,
    flowFromMistakes: flowFromMistakes,
    flowToMistakes: flowToMistakes,
    objectMistakeCount: objectMistakes.length,
    missingObjectsCount: missingObjectsCount,
    flowMistakeCount: flowFromMistakes.length + flowToMistakes.length,
    connectorMistakes: connectorMistakes,
    connectorMistakeCount: connectorMistakeCount,
    missingConnectorCount: missingConnectors.length,
    solutionSignalCount: solutionSignalCount,
    missingConnectors: missingConnectors,
  };

  setWorkingModelErrors(inst, objWMErrors);
};

/** Submit Model functions in assessment mode */
export const setCreatorName = (inst, creatorName) => {
  inst.creator = creatorName;
};

export const setSavedDateTime = inst => {
  inst.savedDateTime = Date.now().toString();
};

export const setNewUUID = inst => {
  inst.uuid = v4();
};
